import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { getIn } from 'formik'
import { useTranslation } from 'react-i18next'

import AdFileUpload from '../../../../../../../../ReusableFormFields/AdFileUpload'
import Field from '../../../../../../../../../components/Form/Field'

import { useMediaUpload } from '../../../../../../../../ReusableFormFields/AdFileUpload/hooks/useMediaUpload'

import { formatImageFile } from '../../../../formatters'
import { getBase64FromFile } from '../../../../../../../../../helpers/files'
import { fileUploadToPlatformSuccess } from '../../../../../../../../../modules/actions/files'
import { getMultipleMediaUploadsValues } from '../../../../../../../../ReusableFormFields/AdFileUpload/helpers'

import {
  MEDIA_FILE_UPLOAD,
  FILE_NAME,
  AD_FILE,
  FILE_URL,
  BASE64
} from '../../../../../../../../ReusableFormFields/AdFileUpload/fields'
import { getGamImageAdValidationSchema } from '../../../../validation'
import {
  CLICK_THROUGH_LINK,
  CLICK_THROUGH_URL_OPTION,
  DIFFERENT_URL_TYPE,
  initialGamFileValues,
  UPLOADED_MEDIA_LIST
} from '../../../../fields'
import { gamImageAccept } from '../../../../constants'

import useStyles from './styles'

function GamImageMediaUpload({ formik, itemPath, onMediaRemove, index, adSizes }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()
  const { setFieldValue, values } = formik

  const { fileURLKey, fileTypeKey, uploadedMediaTypeKey, mediaFile, mediaMissingError, handleMediaRemove } =
    useMediaUpload({
      formik,
      itemPath,
      onMediaRemove,
      initialMediaFileValues: initialGamFileValues,
      errorFieldName: FILE_URL
    })

  const base64 = getIn(values, `${itemPath}[${BASE64}]`)

  const handleMultipleMediasStartUpload = useCallback(
    (files, newMediaItemsValues) => {
      const currentMediasValues = getIn(values, UPLOADED_MEDIA_LIST)

      // this component is shown by initial media item values which added to formik values
      // once new file or files are uploaded this values should be updated with the first file in "files" array
      const updatedMediasValues = getMultipleMediaUploadsValues({
        files,
        newMediaItemsValues,
        currentMediasValues,
        index,
        initialFileValues: initialGamFileValues,
        formatMediaFile: formatImageFile
      })

      setFieldValue(UPLOADED_MEDIA_LIST, updatedMediasValues)

      newMediaItemsValues.forEach(newMediaItem => dispatch(fileUploadToPlatformSuccess(null, newMediaItem[FILE_NAME])))
    },
    [values, index, setFieldValue, dispatch]
  )

  const handleMediaUploaded = useCallback(async () => {
    const base64 = mediaFile?.[AD_FILE] ? await getBase64FromFile(mediaFile[AD_FILE]) : undefined
    const assetFileFields = {
      ...mediaFile,
      [BASE64]: base64,
      [CLICK_THROUGH_LINK]: 'https://'
    }
    setFieldValue(itemPath, assetFileFields)
  }, [itemPath, setFieldValue, mediaFile])

  const imageAdValidationSchema = useMemo(() => getGamImageAdValidationSchema(adSizes), [adSizes])

  return (
    <div className={classes.fileContainer}>
      <AdFileUpload
        values={formik.values}
        fileValidationSchema={imageAdValidationSchema}
        onFileStartUpload={handleMultipleMediasStartUpload}
        onFileUploaded={handleMediaUploaded}
        onFileRemove={handleMediaRemove}
        error={mediaMissingError}
        mediaType={MEDIA_FILE_UPLOAD}
        fileKey={`${itemPath}[${AD_FILE}]`}
        fileNameKey={`${itemPath}[${FILE_NAME}]`}
        fileURLKey={fileURLKey}
        uploadedMediaTypeKey={uploadedMediaTypeKey}
        fileTypeKey={fileTypeKey}
        accept={gamImageAccept}
        multiple={true}
        isDraggable={true}
      />

      {values[CLICK_THROUGH_URL_OPTION] === DIFFERENT_URL_TYPE && base64 && (
        <Field
          className={classes.clickThroughLinkField}
          formik={formik}
          name={`${itemPath}[${CLICK_THROUGH_LINK}]`}
          placeholder={t('Click through link')}
          autoComplete="off"
        />
      )}
    </div>
  )
}

GamImageMediaUpload.propTypes = {
  formik: PropTypes.object.isRequired,
  itemPath: PropTypes.string.isRequired,
  onMediaRemove: PropTypes.func,
  index: PropTypes.number.isRequired,
  adSizes: PropTypes.array.isRequired
}

export default GamImageMediaUpload
